import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FaDownload, FaPenNib, FaSyncAlt, FaTimes } from "react-icons/fa";
import {
  downloadPersonalHandwritingFont,
  exportPersonalHandwriting,
  getPersonalHandwritingProfile,
  previewPersonalHandwriting,
} from "../../api/personalHandwriting";

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Keep the URL alive long enough for browsers to begin reading the attachment.
  window.setTimeout(() => URL.revokeObjectURL(url), 30000);
}

export default function PersonalHandwritingPanel({ authToken }) {
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [status, setStatus] = useState("profile");
  const [message, setMessage] = useState("");
  const [failure, setFailure] = useState(false);
  const [missingCharacters, setMissingCharacters] = useState([]);
  const [reload, setReload] = useState(0);
  const requestRef = useRef(null);
  const busyRef = useRef(false);

  useEffect(() => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    busyRef.current = true;
    setProfiles([]);
    setProfileId("");
    setMarkdown("");
    setPreview(null);
    setMissingCharacters([]);
    setStatus("profile");
    setFailure(false);
    setMessage("");
    getPersonalHandwritingProfile({ authToken, signal: controller.signal }).then((result) => {
      if (controller.signal.aborted) return;
      const available = Array.isArray(result.profiles) && result.profiles.length
        ? result.profiles : result.profile ? [{ ...result.profile, id: result.profile.id || "sunny-notes-demo" }] : [];
      if (result.success && available.length) {
        const selected = available.find((item) => item.id === result.default_profile_id) || available[0];
        setProfiles(available);
        setProfileId(selected.id);
        setMarkdown(selected.sample_markdown || result.sample_markdown || "");
      } else {
        setFailure(true);
        setMessage(result.error || "無法讀取筆跡字型，請重試。");
      }
      setStatus("idle");
      busyRef.current = false;
    });
    return () => {
      controller.abort();
      requestRef.current?.abort();
      busyRef.current = false;
    };
  }, [authToken, reload]);

  useEffect(() => {
    if (!preview?.svg) { setPreviewUrl(""); return undefined; }
    const url = URL.createObjectURL(new Blob([preview.svg], { type: "image/svg+xml" }));
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [preview]);

  const busy = status !== "idle";
  const profile = profiles.find((item) => item.id === profileId);
  const stale = Boolean(preview && (preview.markdown !== markdown || preview.profileId !== profileId));
  const canExport = Boolean(authToken && preview && previewUrl && !stale && !busy && !failure && !missingCharacters.length);
  const supportedCharacters = Array.from(profile?.supported_characters || "");
  const glyphCount = Number.isFinite(profile?.character_count)
    ? profile.character_count : new Set(supportedCharacters).size;
  const personal = profile?.kind === "personal" || profileId === "sunny-notes-demo";

  const reportError = (result) => {
    setFailure(true);
    setMessage(result.error || "處理失敗，請重試。");
    setMissingCharacters(result.missing_characters || []);
  };

  const run = async (kind, action) => {
    if (busyRef.current || !authToken) return;
    const controller = new AbortController();
    requestRef.current = controller;
    busyRef.current = true;
    setStatus(kind);
    setMessage("");
    setFailure(false);
    setMissingCharacters([]);
    try {
      const result = await action(controller.signal);
      if (controller.signal.aborted) return;
      if (!result.success) { reportError(result); return; }
      if (kind === "preview") {
        if (result.profile_id && result.profile_id !== profileId) {
          reportError({ error: "預覽字型與目前選擇不一致，請重新預覽。" });
          return;
        }
        if (!result.svg || result.missing_characters?.length) {
          reportError({ ...result, error: result.missing_characters?.length
            ? "目前字型尚未收錄部分字，請補字後再預覽。" : "預覽內容不完整，請重試。" });
          return;
        }
        setPreview({ svg: result.svg, width: result.width, height: result.height, markdown, profileId });
        setMessage("手寫預覽已更新，可下載這份內容。");
      } else {
        downloadBlob(result.blob, result.filename);
        setMessage(kind === "font" ? "字型已下載。" : "手寫文件已下載。");
      }
    } finally {
      if (requestRef.current === controller && !controller.signal.aborted) {
        setStatus("idle");
        busyRef.current = false;
      }
    }
  };

  const cancel = () => {
    requestRef.current?.abort();
    busyRef.current = false;
    setStatus("idle");
    setFailure(false);
    setMessage("已取消處理，可重新開始。");
  };

  const updateMarkdown = (value) => {
    setMarkdown(value);
    setMissingCharacters([]);
    setMessage("");
    setFailure(false);
  };

  const downloadMarkdown = () => {
    if (!canExport) return;
    downloadBlob(new Blob([preview.markdown], { type: "text/markdown;charset=utf-8" }), `${profile.name}-手寫.md`);
  };

  const changeProfile = (nextId) => {
    if (busyRef.current || !profiles.some((item) => item.id === nextId)) return;
    setProfileId(nextId);
    setPreview(null);
    setMissingCharacters([]);
    setFailure(false);
    setMessage("字型已切換，請產生新的手寫預覽。");
  };

  return (
    <Workspace aria-label="轉手寫工作區">
      <Intro>
        <h3><FaPenNib />把 Markdown 轉成手寫</h3>
        <p>選擇手寫字型，將文字與數學公式排成手寫風格。也可以選「我的字」，使用從你的筆記製作的試用字型。</p>
      </Intro>
      {busy && <Notice role="status">{status === "profile" ? "讀取手寫字型…" : status === "preview" ? "產生手寫預覽…" : "準備下載檔案…"}</Notice>}
      {message && <Notice role={failure ? "alert" : "status"} $error={failure}>
        {message}
        {missingCharacters.length > 0 && <div>缺少字樣：<strong>{missingCharacters.join("、")}</strong></div>}
      </Notice>}
      {busy && <Button type="button" onClick={cancel}><FaTimes />取消處理</Button>}
      {!profile && !busy && <Button type="button" disabled={!authToken} onClick={() => setReload((value) => value + 1)}><FaSyncAlt />重新讀取字型</Button>}
      {profile && <>
        <ProfileBar>
          <StyleField><label htmlFor="handwriting-style-select">手寫字型</label>
            <Select id="handwriting-style-select" value={profileId} disabled={busy} onChange={(event) => changeProfile(event.target.value)}>
              {profiles.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </Select>
            <span>{glyphCount.toLocaleString()} 個已收錄字樣{profile.version ? ` · ${profile.version}` : ""}</span>
          </StyleField>
          <Actions>
            {(["ttf", "woff2"].filter((format) => profile.formats?.includes(format))).map((format) => (
              <Button key={format} type="button" disabled={busy || !authToken}
                onClick={() => run("font", (signal) => downloadPersonalHandwritingFont({ format, profileId, authToken, signal }))}>
                <FaDownload />下載 {format.toUpperCase()} 字型
              </Button>
            ))}
          </Actions>
        </ProfileBar>
        {profile.description && <Hint>{profile.description}</Hint>}
        {personal && <Hint>「我的字」目前只收錄已從筆記取出的字樣；未收錄的字需要補字，目前不會自動生成你沒寫過的字。</Hint>}
        <Details><summary>字型收錄範圍與使用說明</summary>
          {supportedCharacters.length > 0 && <GlyphList>{supportedCharacters.slice(0, 120).join("")}{supportedCharacters.length > 120 ? "…" : ""}</GlyphList>}
          {(profile.limitations || []).length > 0 && <ul>{profile.limitations.map((limitation, index) => <li key={index}>{limitation}</li>)}</ul>}
        </Details>
        <Columns>
          <Panel>
            <label htmlFor="personal-handwriting-markdown">Markdown 與數學公式</label>
            <TextArea id="personal-handwriting-markdown" rows={13} maxLength={4000} value={markdown} disabled={busy}
              onChange={(event) => updateMarkdown(event.target.value)} spellCheck={false} />
            <Hint>每次最多 4,000 字。先用範例測試；公式可用 $…$ 或 $$…$$。預覽時會檢查文字與公式的字樣是否已收錄。</Hint>
            <Actions>
              <Button type="button" $primary disabled={busy || !authToken || !markdown.trim()}
                onClick={() => run("preview", (signal) => previewPersonalHandwriting({ markdown, profileId, authToken, signal }))}>
                <FaPenNib />{preview ? "更新手寫預覽" : "產生手寫預覽"}
              </Button>
              <Button type="button" disabled={busy} onClick={() => updateMarkdown(profile.sample_markdown || "")}>載入範例</Button>
            </Actions>
          </Panel>
          <Panel>
            <PreviewHeading><h3>{profile.name}預覽</h3>{stale && <span>內容已修改</span>}</PreviewHeading>
            <PreviewFrame $stale={stale}>
              {preview && previewUrl ? <img src={previewUrl} alt="手寫 Markdown 與數學公式預覽" width={preview.width} height={preview.height} />
                : <Empty>產生預覽後，這裡會顯示所選字型的手寫文字與公式。</Empty>}
            </PreviewFrame>
            <Hint>{stale ? "請更新手寫預覽後再下載，確保匯出的是目前內容與字型。" : "PDF、PNG 與 MD 會使用目前已成功預覽的內容。"}</Hint>
            <Actions>
              {["pdf", "png"].map((format) => <Button key={format} type="button" disabled={!canExport}
                onClick={() => run("export", (signal) => exportPersonalHandwriting({ markdown: preview.markdown, profileId: preview.profileId, format, authToken, signal }))}>
                <FaDownload />下載 {format.toUpperCase()}
              </Button>)}
              <Button type="button" disabled={!canExport} onClick={downloadMarkdown}><FaDownload />下載 MD</Button>
            </Actions>
          </Panel>
        </Columns>
      </>}
    </Workspace>
  );
}

const Workspace = styled.section`display: grid; gap: 14px; color: var(--app-text, #222);`;
const Intro = styled.div`
  h3 { display: flex; align-items: center; gap: 9px; margin: 0 0 8px; font-size: 19px; }
  p { margin: 0; color: var(--app-muted-text, #626262); line-height: 1.7; }
`;
const Columns = styled.div`display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 16px; @media(max-width: 800px) { grid-template-columns: 1fr; }`;
const Panel = styled.div`
  display: grid; align-content: start; gap: 12px; min-width: 0; padding: 16px;
  border: 1px solid var(--app-border, #d6d6d6); border-radius: 8px; background: var(--app-surface, #fbfbfb);
  label, h3 { margin: 0; font-size: 15px; font-weight: 750; }
`;
const TextArea = styled.textarea`
  width: 100%; box-sizing: border-box; resize: vertical; padding: 11px; line-height: 1.6; min-height: 240px;
  border: 1px solid var(--app-border, #ccc); border-radius: 6px;
  color: var(--app-input-text, #222); background: var(--app-input-bg, #fff);
  font: 14px/1.6 ui-monospace, SFMono-Regular, Menlo, monospace;
  &:focus { outline: 2px solid var(--app-icon-accent, #537caa); outline-offset: 1px; }
`;
const Actions = styled.div`display: flex; gap: 8px; flex-wrap: wrap;`;
const Button = styled.button`
  display: inline-flex; justify-content: center; align-items: center; gap: 7px; padding: 8px 12px;
  border: 1px solid var(--app-border, #c6c6c6); border-radius: 6px; font-weight: 700; cursor: pointer;
  color: ${({ $primary }) => $primary ? "var(--app-nav-active-text, #fff)" : "var(--app-text, #222)"};
  background: ${({ $primary }) => $primary ? "var(--app-nav-active-bg, #242424)" : "var(--app-input-bg, #fff)"};
  &:disabled { opacity: .45; cursor: not-allowed; }
`;
const ProfileBar = styled.div`
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px;
  > div:first-child { display: grid; gap: 3px; }
  span { color: var(--app-muted-text, #626262); font-size: 13px; }
`;
const StyleField = styled.div`min-width: 220px; label { font-size: 14px; font-weight: 750; }`;
const Select = styled.select`
  padding: 9px 11px; min-height: 40px; border: 1px solid var(--app-border, #ccc); border-radius: 6px;
  color: var(--app-input-text, #222); background: var(--app-input-bg, #fff); font: inherit;
  &:focus { outline: 2px solid var(--app-icon-accent, #537caa); outline-offset: 1px; }
  &:disabled { opacity: .5; }
`;
const Details = styled.details`
  border: 1px solid var(--app-border, #d6d6d6); padding: 10px 14px; border-radius: 7px; font-size: 13px;
  summary { cursor: pointer; font-weight: 700; }
  ul { margin-bottom: 0; padding-left: 20px; line-height: 1.7; }
`;
const GlyphList = styled.div`margin-top: 10px; overflow-wrap: anywhere; line-height: 1.8; letter-spacing: 3px;`;
const Hint = styled.p`margin: 0; color: var(--app-muted-text, #626262); font-size: 13px; line-height: 1.6;`;
const Notice = styled.div`
  border: 1px solid ${({ $error }) => $error ? "#d7aaa3" : "var(--app-border, #d6d6d6)"};
  border-radius: 7px; padding: 10px 12px; line-height: 1.6; overflow-wrap: anywhere;
  color: ${({ $error }) => $error ? "var(--app-danger-text, #963128)" : "var(--app-text, #222)"};
  background: var(--app-surface-soft, #f4f4f4);
`;
const PreviewHeading = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 10px; span { color: var(--app-muted-text, #626262); font-size: 12px; }`;
const PreviewFrame = styled.div`
  min-height: 240px; overflow: auto; border: 1px solid var(--app-border, #ddd); border-radius: 6px; background: #fff;
  opacity: ${({ $stale }) => $stale ? .6 : 1};
  img { display: block; width: 100%; height: auto; }
`;
const Empty = styled.div`padding: 45px 20px; text-align: center; color: #666; font-size: 14px; line-height: 1.6;`;
